import React from "react";
//#region types
import { OtherSettingsProps } from "models/propTypes/invoicePropTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { NestedKeyOf } from "models/utilTypes/nestedKeyType";
import { InvoiceFormTypes } from "models/formTypes";
//#endregion
//#region components
import { WizardSection } from "components/common/wintForms";
import DateGroup from "./dateGroup";
import DisplayGroup from "./displayGroup";
import MessageGroup from "./messageGroup";
import OtherGroup from "./otherGroup";
import ReminderGroup from "./reminderGroup";
import { Grid } from "@mui/material";
import { useInvoiceService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

const OtherSettingsStep: React.FC<OtherSettingsProps> = (props) => {
  const invoiceService = useInvoiceService();
  const { connect, values, submitted, wizardProps, isRecurring, creditedInvoiceId, pendingAttachments } = props;

  const [paymentOptions, setPaymentOptions] = React.useState<WintSuperkollAPIModel.InvoicePaymentOption[]>();
  const [sellers, setSellers] = React.useState<string[]>();

  React.useEffect(() => {
    invoiceService.getPaymentOptions().then((res) => {
      setPaymentOptions(res ?? []);
      configDefaultPaymentOptions(res ?? []);
    });

    invoiceService.getSellers().then((res) => {
      setSellers(res ?? []);
    });
  }, []);

  React.useEffect(() => {
    if (paymentOptions && values.quotationSerialNumber) {
      configDefaultPaymentOptions(paymentOptions);
    }
  }, [values.quotationSerialNumber]);

  const configDefaultPaymentOptions = (paymentOptions: WintSuperkollAPIModel.InvoicePaymentOption[]) => {
    if (values.otherSettings.paymentMethodId === undefined || values.quotationSerialNumber) {
      const defaultPaymentOption = paymentOptions?.find((p) => p.isDefault);

      if (defaultPaymentOption !== undefined) {
        const paymentMethodFieldName: NestedKeyOf<InvoiceFormTypes.Create> = "otherSettings.paymentMethodId";
        connect(paymentMethodFieldName).setValue(defaultPaymentOption.id, true);
      }
    }
  };

  const hasErrors = connect("otherSettings").hasErrors;
  const contentSummary = !hasErrors
    ? [
        values.otherSettings.invoiceLayout !== undefined
          ? LocalizationHelperInstance.getFormattedMessage(
              `Invoice.InvoiceLayout_${values.otherSettings.invoiceLayout}`
            )
          : undefined,
        values.otherSettings.languageKey !== undefined
          ? LocalizationHelperInstance.getFormattedMessage(
              `Language.${(values.otherSettings.languageKey as string)?.toUpperCase()}`
            )
          : undefined,
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Etc),
      ]
        .filter((s) => s)
        .join(", ")
    : "";

  return (
    <WizardSection
      header={TranslationKeys.Invoice.OtherSettings}
      description={contentSummary ?? TranslationKeys.Invoice.ReceiverDescription}
      submitted={submitted}
      hasErrors={hasErrors}
      wizardProps={wizardProps}
      testId="otherSettingsStep"
    >
      <Grid container direction="column">
        {!isRecurring && !creditedInvoiceId && <ReminderGroup connect={connect} values={values} />}
        {!isRecurring && <DateGroup connect={connect} values={values} />}
        <MessageGroup connect={connect} values={values} pendingAttachments={pendingAttachments} />
        <DisplayGroup connect={connect} values={values} />
        <OtherGroup connect={connect} values={values} paymentOptions={paymentOptions} sellers={sellers} />
      </Grid>
    </WizardSection>
  );
};

export default OtherSettingsStep;
